import { createMemo, createSignal, For, onMount, Show } from "solid-js";
import { api, type CacheEntryDetail, type CacheEntryStatus, type CacheEntrySummary, type Project } from "../shared";

function formatDate(value: string | null | undefined) {
  return value ? new Date(value).toLocaleString() : "-";
}

function formatBytes(value: number | null | undefined) {
  const size = value ?? 0;
  if (!size) return "0 B";
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

function ttlSeconds(entry: CacheEntrySummary) {
  if (!entry.generatedAt || !entry.expiresAt) return null;
  const generatedAt = Date.parse(entry.generatedAt);
  const expiresAt = Date.parse(entry.expiresAt);
  if (!Number.isFinite(generatedAt) || !Number.isFinite(expiresAt) || expiresAt < generatedAt) return null;
  return Math.round((expiresAt - generatedAt) / 1000);
}

export function OrganizationCacheTab(props: {
  organizationId: string;
  projects: Project[];
}) {
  const [entries, setEntries] = createSignal<CacheEntrySummary[]>([]);
  const [loading, setLoading] = createSignal(false);
  const [error, setError] = createSignal("");
  const [selectedId, setSelectedId] = createSignal<string | null>(null);
  const [detail, setDetail] = createSignal<CacheEntryDetail | null>(null);
  const [detailLoading, setDetailLoading] = createSignal(false);
  const [cacheTypeFilter, setCacheTypeFilter] = createSignal("");
  const [endpointFilter, setEndpointFilter] = createSignal("");
  const [statusFilter, setStatusFilter] = createSignal<"" | CacheEntryStatus>("");
  const [projectFilter, setProjectFilter] = createSignal("");

  const projectNameById = createMemo(() =>
    new Map(props.projects.map((project) => [project.id, project.name])),
  );

  const filteredEntries = createMemo(() =>
    entries().filter((entry) =>
      (!cacheTypeFilter() || entry.cacheType === cacheTypeFilter())
      && (!endpointFilter() || entry.endpoint === endpointFilter())
      && (!statusFilter() || entry.status === statusFilter())
      && (!projectFilter() || entry.projectId === projectFilter())
    ),
  );

  const cacheTypes = createMemo(() => Array.from(new Set(entries().map((entry) => entry.cacheType))).sort());
  const endpoints = createMemo(() => Array.from(new Set(entries().map((entry) => entry.endpoint))).sort());

  async function loadEntries() {
    setLoading(true);
    setError("");
    try {
      setEntries(await api.caches.list({ organizationId: props.organizationId }));
    } catch (loadError: any) {
      setError(loadError?.message ?? "Failed to load caches");
    } finally {
      setLoading(false);
    }
  }

  async function loadDetail(id: string) {
    if (selectedId() === id && detail()) {
      setSelectedId(null);
      setDetail(null);
      return;
    }
    setSelectedId(id);
    setDetailLoading(true);
    setError("");
    try {
      setDetail(await api.caches.get(id));
    } catch (detailError: any) {
      setDetail(null);
      setError(detailError?.message ?? "Failed to load cache detail");
    } finally {
      setDetailLoading(false);
    }
  }

  async function invalidateEntry(id: string) {
    try {
      await api.caches.invalidate(id);
      await loadEntries();
      if (selectedId() === id) {
        await loadDetail(id);
      }
    } catch (invalidateError: any) {
      setError(invalidateError?.message ?? "Failed to invalidate cache entry");
    }
  }

  async function invalidateScope() {
    try {
      if (projectFilter()) {
        await api.caches.invalidateProject(projectFilter(), cacheTypeFilter() || undefined);
      } else {
        await api.organizations.invalidateCaches(props.organizationId, cacheTypeFilter() || undefined);
      }
      await loadEntries();
      if (selectedId()) {
        setDetail(await api.caches.get(selectedId()!));
      }
    } catch (invalidateError: any) {
      setError(invalidateError?.message ?? "Failed to invalidate caches");
    }
  }

  onMount(() => {
    void loadEntries();
  });

  return (
    <div class="space-y-3">
      <div class="flex items-center justify-between gap-3">
        <div>
          <h3 class="text-sm font-semibold text-gray-400 uppercase tracking-wide">Organization Cache</h3>
          <p class="text-xs text-gray-500">Inspect cache entries by type, endpoint, and canonical key. Fetch payloads on demand and invalidate one cache, one project, or the filtered organization scope.</p>
        </div>
        <button onClick={() => void invalidateScope()} class="text-xs px-3 py-1 bg-amber-900/30 border border-amber-700 text-amber-300 rounded hover:bg-amber-900/50 cursor-pointer">
          {projectFilter() ? "Invalidate Project Scope" : "Invalidate Org Scope"}
        </button>
      </div>

      <div class="grid gap-3 md:grid-cols-4">
        <label class="text-xs text-gray-400">
          <div class="mb-1">Cache Type</div>
          <select value={cacheTypeFilter()} onChange={(e) => setCacheTypeFilter(e.currentTarget.value)} class="w-full rounded border border-gray-800 bg-gray-950 px-3 py-2 text-sm text-gray-200 cursor-pointer">
            <option value="">All types</option>
            <For each={cacheTypes()}>{(value) => <option value={value}>{value}</option>}</For>
          </select>
        </label>
        <label class="text-xs text-gray-400">
          <div class="mb-1">Endpoint</div>
          <select value={endpointFilter()} onChange={(e) => setEndpointFilter(e.currentTarget.value)} class="w-full rounded border border-gray-800 bg-gray-950 px-3 py-2 text-sm text-gray-200 cursor-pointer">
            <option value="">All endpoints</option>
            <For each={endpoints()}>{(value) => <option value={value}>{value}</option>}</For>
          </select>
        </label>
        <label class="text-xs text-gray-400">
          <div class="mb-1">Status</div>
          <select value={statusFilter()} onChange={(e) => setStatusFilter(e.currentTarget.value as "" | CacheEntryStatus)} class="w-full rounded border border-gray-800 bg-gray-950 px-3 py-2 text-sm text-gray-200 cursor-pointer">
            <option value="">All statuses</option>
            <option value="fresh">Fresh</option>
            <option value="stale">Stale</option>
            <option value="invalidated">Invalidated</option>
          </select>
        </label>
        <label class="text-xs text-gray-400">
          <div class="mb-1">Project</div>
          <select value={projectFilter()} onChange={(e) => setProjectFilter(e.currentTarget.value)} class="w-full rounded border border-gray-800 bg-gray-950 px-3 py-2 text-sm text-gray-200 cursor-pointer">
            <option value="">All projects</option>
            <For each={props.projects}>{(project) => <option value={project.id}>{project.name}</option>}</For>
          </select>
        </label>
      </div>

      <Show when={error()}>
        <div class="rounded border border-red-900/50 bg-red-950/30 px-3 py-2 text-sm text-red-300">{error()}</div>
      </Show>

      <div class="border border-gray-800 rounded-lg overflow-x-auto max-h-[28rem] overflow-y-auto">
        <table class="w-full text-sm">
          <thead class="bg-gray-900 text-gray-400 sticky top-0">
            <tr>
              <th class="text-left px-4 py-3 font-medium">Type</th>
              <th class="text-left px-4 py-3 font-medium">Endpoint</th>
              <th class="text-left px-4 py-3 font-medium">Key</th>
              <th class="text-left px-4 py-3 font-medium">Project</th>
              <th class="text-left px-4 py-3 font-medium">Status</th>
              <th class="text-left px-4 py-3 font-medium">Size</th>
              <th class="text-left px-4 py-3 font-medium">Generated</th>
              <th class="text-left px-4 py-3 font-medium">TTL</th>
              <th class="text-left px-4 py-3 font-medium">Expires</th>
              <th class="text-right px-4 py-3 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-800">
            <Show when={!loading()} fallback={<tr><td colspan="10" class="px-4 py-6 text-sm text-gray-500">Loading caches...</td></tr>}>
              <For each={filteredEntries()}>
                {(entry) => (
                  <tr class="hover:bg-gray-900/50">
                    <td class="px-4 py-3 text-gray-200 font-mono text-xs">{entry.cacheType}</td>
                    <td class="px-4 py-3 text-xs text-gray-300">{entry.endpoint}</td>
                    <td class="px-4 py-3 text-xs text-gray-400 font-mono">{entry.cacheKey}</td>
                    <td class="px-4 py-3 text-xs text-gray-300">{entry.projectId ? (projectNameById().get(entry.projectId) ?? entry.projectId) : "-"}</td>
                    <td class="px-4 py-3 text-xs text-gray-300 capitalize">{entry.status}</td>
                    <td class="px-4 py-3 text-xs text-gray-300">{formatBytes(entry.payloadBytes)}</td>
                    <td class="px-4 py-3 text-xs text-gray-400">{formatDate(entry.generatedAt)}</td>
                    <td class="px-4 py-3 text-xs text-gray-400">{ttlSeconds(entry) !== null ? `${ttlSeconds(entry)} s` : "-"}</td>
                    <td class="px-4 py-3 text-xs text-gray-400">{formatDate(entry.expiresAt)}</td>
                    <td class="px-4 py-3 text-right space-x-1">
                      <button onClick={() => void loadDetail(entry.id)} class="text-xs px-2 py-0.5 bg-gray-800 text-gray-300 rounded hover:bg-gray-700 cursor-pointer">
                        {selectedId() === entry.id ? "Hide Value" : "View Value"}
                      </button>
                      <button onClick={() => void invalidateEntry(entry.id)} class="text-xs px-2 py-0.5 bg-amber-900/30 border border-amber-700 text-amber-300 rounded hover:bg-amber-900/50 cursor-pointer">
                        Invalidate
                      </button>
                    </td>
                  </tr>
                )}
              </For>
            </Show>
          </tbody>
        </table>
      </div>

      <Show when={detailLoading()}>
        <div class="text-sm text-gray-500">Loading cache value...</div>
      </Show>

      <Show when={detail() && !detailLoading()}>
        <div class="border border-gray-800 rounded-lg bg-gray-950 p-4 space-y-3">
          <div class="flex items-center justify-between gap-3">
            <div>
              <h4 class="text-sm font-semibold text-gray-200">{detail()!.cacheType}</h4>
              <div class="text-xs text-gray-500 font-mono">{detail()!.endpoint}</div>
              <div class="text-xs text-gray-500 font-mono">{detail()!.cacheKey}</div>
            </div>
            <div class="text-xs text-gray-500 text-right">
              <div>Refresh lock: {formatDate(detail()!.refreshStartedAt)}</div>
              <div>Last accessed: {formatDate(detail()!.lastAccessedAt)}</div>
            </div>
          </div>

          <div class="grid gap-3 md:grid-cols-4 text-xs">
            <div class="rounded border border-gray-800 bg-gray-900 p-3">
              <div class="text-gray-500">Project</div>
              <div class="mt-1 text-gray-200">{detail()!.projectId ? (projectNameById().get(detail()!.projectId!) ?? detail()!.projectId) : "-"}</div>
            </div>
            <div class="rounded border border-gray-800 bg-gray-900 p-3">
              <div class="text-gray-500">Payload Size</div>
              <div class="mt-1 text-gray-200">{formatBytes(detail()!.payloadBytes)}</div>
            </div>
            <div class="rounded border border-gray-800 bg-gray-900 p-3">
              <div class="text-gray-500">Status</div>
              <div class="mt-1 text-gray-200 capitalize">{detail()!.status}</div>
            </div>
            <div class="rounded border border-gray-800 bg-gray-900 p-3">
              <div class="text-gray-500">Has Payload</div>
              <div class="mt-1 text-gray-200">{detail()!.hasPayload ? "Yes" : "No"}</div>
            </div>
          </div>

          <div>
            <div class="mb-2 text-xs font-medium text-gray-400 uppercase tracking-wide">Cache Value</div>
            <pre class="max-h-96 overflow-auto rounded border border-gray-800 bg-gray-900 p-3 text-[11px] leading-5 text-gray-200">{JSON.stringify(detail()!.payload, null, 2)}</pre>
          </div>
        </div>
      </Show>
    </div>
  );
}
